import React from 'react';
import { TicketTypeAvailabilityBehavior } from '@/dashboard/enums/tickets';
import { formatSaleDate } from '@repo/checkout/lib/formatSaleDate';
import getPageTheme from '@repo/checkout/lib/theming/getPageTheme';

function TicketTypeSaleDate({
  page,
  ticketType,
  ticketGroup,
  allTicketTypes = [],
  className = 'text-sm',
}) {
  if (ticketType.availabilityStatus === 'unavailable' || ticketGroup.status === 'unavailable')
    return null;

  const now = new Date();

  const typeAvailabilityBehavior =
    ticketType.availabilityBehavior || TicketTypeAvailabilityBehavior.ALWAYS_AVAILABLE;

  const availabilityBehavior = typeAvailabilityBehavior;

  const dateBasedBehaviors = [
    TicketTypeAvailabilityBehavior.UNTIL_EVENT_STARTS,
    TicketTypeAvailabilityBehavior.DATE_TIME_BASED,
    TicketTypeAvailabilityBehavior.AFTER_TICKET_SOLD_OUT,
    TicketTypeAvailabilityBehavior.AFTER_TICKET_SALE_ENDS,
    TicketTypeAvailabilityBehavior.AFTER_TICKET_ENDS_OR_SOLD_OUT,
  ];
  if (!dateBasedBehaviors.includes(availabilityBehavior)) {
    return null;
  }

  return (
    <div className={`${className} text-gray-500 dark:text-muted-foreground w-full`}>
      {getTicketTypeDateMessage(ticketType, allTicketTypes, now, page)}
    </div>
  );
}

function formatSaleDateForDashboard(startDate, endDate, page, now = new Date()) {
  const theme = getPageTheme(page);
  const result = formatSaleDate(startDate, endDate, {
    now,
    timezone: page.eventDetails?.timezone,
    locale: theme.locale,
    showTimezone: true,
    startText: 'Starts',
    startedText: 'Started on',
    endText: 'Ends',
    endedText: 'Ended on',
    showBothMessages: true,
  });

  // Convert array of messages to JSX elements
  if (Array.isArray(result)) {
    return (
      <>
        {result.map((message, index) => (
          <div key={index}>{message}</div>
        ))}
      </>
    );
  }

  return result ? <div>{result}</div> : null;
}

function getTicketTypeDateMessage(ticketType, allTicketTypes, now, page) {
  const availabilityBehavior =
    ticketType.availabilityBehavior || TicketTypeAvailabilityBehavior.ALWAYS_AVAILABLE;

  switch (availabilityBehavior) {
    case TicketTypeAvailabilityBehavior.UNTIL_EVENT_STARTS: {
      if (page && page.eventDetails && page.eventDetails.startDate) {
        const eventStartDate = new Date(page.eventDetails.startDate);
        return formatSaleDateForDashboard(null, eventStartDate, page, now);
      }
      return null;
    }
    case TicketTypeAvailabilityBehavior.DATE_TIME_BASED: {
      return formatSaleDateForDashboard(ticketType.saleStartOn, ticketType.saleEndOn, page, now);
    }

    case TicketTypeAvailabilityBehavior.AFTER_TICKET_SOLD_OUT: {
      if (ticketType.triggerTicketType) {
        const referencedType = allTicketTypes.find(tt => tt._id === ticketType.triggerTicketType);

        const message = formatSaleDateForDashboard(null, ticketType.saleEndOn, page, now);

        if (referencedType && referencedType.status === 'enabled') {
          return (
            <>
              <div>
                Starts if <span className="font-semibold">{referencedType.name}</span> sells out
              </div>
              {message}
            </>
          );
        }
      }
      break;
    }

    case TicketTypeAvailabilityBehavior.AFTER_TICKET_SALE_ENDS: {
      if (ticketType.triggerTicketType) {
        const referencedType = allTicketTypes.find(tt => tt._id === ticketType.triggerTicketType);
        if (referencedType && referencedType.status === 'enabled' && referencedType.saleEndOn) {
          const message = formatSaleDateForDashboard(
            ticketType.saleStartOn,
            ticketType.saleEndOn,
            page,
            now,
          );
          return (
            <>
              <div>
                When <span className="font-semibold">{referencedType.name}</span> sale ends
              </div>
              {message}
            </>
          );
        }
      }
      break;
    }

    case TicketTypeAvailabilityBehavior.AFTER_TICKET_ENDS_OR_SOLD_OUT: {
      if (ticketType.triggerTicketType) {
        const referencedType = allTicketTypes.find(tt => tt._id === ticketType.triggerTicketType);
        if (referencedType && referencedType.status === 'enabled') {
          const message = formatSaleDateForDashboard(
            ticketType.saleStartOn,
            ticketType.saleEndOn,
            page,
            now,
          );
          return (
            <>
              <div>
                <div>
                  Starts if <span className="font-semibold">{referencedType.name}</span> sells out
                  {referencedType.saleEndOn != null && ' or,'}
                </div>
                {message}
              </div>
            </>
          );
        }
      }
      break;
    }
    default: {
      return null;
    }
  }
}

export default TicketTypeSaleDate;
